import {
  BookBookmarkIcon,
  CaretDownIcon,
  CaretLeftIcon,
  CheckIcon,
  ClockCounterClockwiseIcon,
  CodeIcon,
  FileIcon,
  GitBranchIcon,
  GitDiffIcon,
  TrayIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { AutoRefresh } from '@/components/auto-refresh';
import { ChangeDetail } from '@/components/change-detail';
import { ChangeList } from '@/components/change-list';
import { CommitDetail, CommitDetailSkeleton } from '@/components/commit-detail';
import { DiffViewer } from '@/components/diff-viewer';
import { ErrorState } from '@/components/error-state';
import { FileTree } from '@/components/file-tree';
import { FileViewer } from '@/components/file-viewer';
import { GettingStartedGuide } from '@/components/getting-started-guide';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { FaroErrorBoundary, pushError } from '@/lib/faro';
import { orgBySlugQueryOptions } from '@/lib/org-query-options';
import { repoQueryOptions } from '@/lib/repo-query-options';
import { repoSnippetsQueryOptions } from '@/lib/snippets';
import {
  vcsBranchesQueryOptions,
  vcsCommitQueryOptions,
  vcsContentQueryOptions,
  vcsFileContentQueryOptions,
} from '@/lib/vcs-query-options';
import { loadRepoDetailPage } from '@/server/functions/pages/repo-detail';
import { updateRepoServerFn } from '@/server/functions/repo';
import type { BranchInfo } from '@/server/functions/vcs';
import { listChanges } from '@/server/functions/vcs';

const searchSchema = z.object({
  path: z.string().optional(),
  ref: z.string().optional(),
  tab: z.enum(['code', 'diff', 'commits']).catch('code').optional(),
  compare: z.string().optional(),
  commit: z.string().optional(),
});

function RepoDetailSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-4 w-4 rounded" />
        <Skeleton className="h-6 w-40 rounded" />
        <Skeleton className="h-6 w-64 rounded" />
      </div>

      <div className="flex items-center gap-1 border-b border-mesa-border">
        <div className="px-4 py-2 border-b-2 border-mesa-accent -mb-px">
          <Skeleton className="h-4 w-12 rounded" />
        </div>
        <div className="px-4 py-2 -mb-px">
          <Skeleton className="h-4 w-16 rounded" />
        </div>
      </div>

      <div className="flex gap-4 border border-mesa-border overflow-hidden">
        <div className="w-64 border-r border-mesa-border bg-mesa-bg-subtle p-2 shrink-0 space-y-1">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1.5">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-4 rounded" style={{ width: `${[55, 40, 70, 35, 60, 45][i]}%` }} />
            </div>
          ))}
        </div>
        <div className="flex-1 p-4 flex items-center justify-center">
          <Skeleton className="h-4 w-48 rounded" />
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/$orgSlug/repositories_/$name')({
  validateSearch: searchSchema,
  ssr: 'data-only',
  loaderDeps: ({ search: { ref } }) => ({ ref }),
  loader: async ({ context: { queryClient, org }, params: { name }, deps: { ref } }) => {
    const [pageData] = await Promise.all([
      queryClient.ensureQueryData({
        queryKey: ['repo-detail-page', org.slug, name, ref],
        queryFn: () => loadRepoDetailPage({ data: { orgSlug: org.slug, name, ref } }),
        staleTime: 10_000,
      }),
      queryClient.ensureQueryData(repoSnippetsQueryOptions()),
    ]);

    // Seed granular caches for component-level queries and mutation invalidation
    queryClient.setQueryData(['repo', org.slug, name], {
      repo: pageData.repo,
      tags: pageData.tags,
    });

    if (pageData.rootContent && pageData.headOid) {
      queryClient.setQueryData(['vcs-content', org.slug, pageData.repo.id, pageData.headOid, ''], pageData.rootContent);
    }

    if (pageData.branches) {
      queryClient.setQueryData(
        ['vcs-branches', org.slug, pageData.repo.id, pageData.repo.defaultBranch],
        pageData.branches
      );
    }

    return pageData;
  },
  pendingComponent: RepoDetailSkeleton,
  errorComponent: ErrorState,
  component: RepoDetailPage,
});

function CommitErrorFallback({ onBack }: { onBack: () => void }) {
  return (
    <div className="text-center py-12 border border-mesa-border">
      <WarningCircleIcon className="h-8 w-8 mx-auto mb-2 text-mesa-muted" />
      <p className="text-mesa-muted mb-4">Failed to load commit details</p>
      <Button variant="outline" size="sm" onClick={onBack}>
        Back to commits
      </Button>
    </div>
  );
}

function RepoDetailPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const { name, orgSlug } = Route.useParams();
  const { org: routeOrg } = Route.useRouteContext();
  const { data: queryOrg } = useSuspenseQuery(orgBySlugQueryOptions(orgSlug));
  const org = queryOrg ?? routeOrg;

  const pageData = Route.useLoaderData();
  const isEmpty = !pageData.headOid;

  const { data: repoData } = useSuspenseQuery(repoQueryOptions(org.slug, name));
  const repo = repoData.repo;
  const { data: snippets } = useSuspenseQuery(repoSnippetsQueryOptions());

  const { data: branchesResponse } = useQuery({
    ...vcsBranchesQueryOptions(org.slug, repo.id, repo.defaultBranch),
    placeholderData: keepPreviousData,
  });

  const branches = useMemo(() => branchesResponse?.branches ?? [], [branchesResponse?.branches]);
  const defaultBranch = useMemo(() => branches.find((branch: BranchInfo) => branch.is_default)?.name, [branches]);
  const headOid = useMemo(() => branches.find((b) => b.is_default)?.head_oid, [branches]);

  const currentRef = search.ref || defaultBranch || repo.defaultBranch || 'main';
  const compareBase = defaultBranch ?? currentRef;
  const branchesLoaded = !!branchesResponse;
  const hasComparableBranches = !branchesLoaded || branches.length > 1;

  const selectedOid = useMemo(() => {
    if (!search.ref) return undefined;
    return branches.find((b) => b.name === search.ref)?.head_oid;
  }, [search.ref, branches]);

  // If ref is set but doesn't exist in branches (deleted/renamed), clear it
  useEffect(() => {
    if (search.ref && branchesLoaded && !selectedOid) {
      navigate({ search: (prev) => ({ ...prev, ref: undefined }), replace: true });
    }
  }, [search.ref, branchesLoaded, selectedOid, navigate]);

  const { data: contentResponse } = useQuery({
    ...vcsContentQueryOptions(org.slug, repo.id, (selectedOid ?? headOid)!),
    enabled: !isEmpty && (!search.ref || !!selectedOid),
  });

  const entries = useMemo(() => (contentResponse?.type === 'dir' ? contentResponse.entries : []), [contentResponse]);

  const queryClient = useQueryClient();
  const oid = selectedOid ?? headOid ?? undefined;

  // Prefetch README content so it's instant when the user clicks it
  useEffect(() => {
    const readme = entries.find((e) => e.type === 'file' && e.name.toLowerCase().startsWith('readme'));
    if (readme && oid) {
      queryClient.prefetchQuery(vcsFileContentQueryOptions(org.slug, repo.id, oid, readme.path));
    }
  }, [entries, queryClient, org.slug, repo.id, oid]);

  const selectedPath = search.path;
  const activeTab = search.tab === 'diff' && !hasComparableBranches ? 'code' : search.tab || 'code';
  const compareTarget = search.compare;
  const [setDefaultError, setSetDefaultError] = useState<string | null>(null);
  const [settingDefaultBranch, setSettingDefaultBranch] = useState<string | null>(null);
  const settingDefaultRef = useRef(false);

  const {
    data: changesData,
    fetchNextPage,
    hasNextPage,
    isFetching: commitsFetching,
  } = useInfiniteQuery({
    queryKey: ['changes', org.slug, repo.id, currentRef, selectedOid ?? headOid],
    queryFn: ({ pageParam }) =>
      listChanges({
        data: {
          orgSlug: org.slug,
          repoId: repo.id,
          startOid: (selectedOid ?? headOid)!,
          pageSize: 20,
          cursor: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage?.next_cursor ?? undefined,
    enabled: activeTab === 'commits' && !!(selectedOid ?? headOid),
  });

  const allChanges = useMemo(() => changesData?.pages.flatMap((page) => page.changes) ?? [], [changesData]);

  const changeFromList = useMemo(
    () => (search.commit ? (allChanges.find((c) => c.current_commit_oid === search.commit) ?? null) : null),
    [search.commit, allChanges]
  );
  // A selected commit that belongs to a change in the list is rendered as a
  // change (ChangeDetail); only a commit that isn't a change's current commit
  // falls back to fetching + rendering it as a plain commit.
  const { data: fetchedCommit, isError: commitFetchError } = useQuery({
    ...vcsCommitQueryOptions(org.slug, repo.id, search.commit ?? ''),
    enabled: !!search.commit && !changeFromList,
  });
  const selectedCommit = fetchedCommit ?? null;

  const handleRefresh = useCallback(async () => {
    const prevData = queryClient.getQueryData<Awaited<ReturnType<typeof loadRepoDetailPage>>>([
      'repo-detail-page',
      org.slug,
      name,
      search.ref,
    ]);
    const prevHeadOid = prevData?.headOid;

    const freshData = await queryClient.fetchQuery({
      queryKey: ['repo-detail-page', org.slug, name, search.ref],
      queryFn: () => loadRepoDetailPage({ data: { orgSlug: org.slug, name, ref: search.ref } }),
      staleTime: 0,
    });

    queryClient.setQueryData(['repo', org.slug, name], {
      repo: freshData.repo,
      tags: freshData.tags,
    });

    if (freshData.rootContent && freshData.headOid) {
      queryClient.setQueryData(
        ['vcs-content', org.slug, freshData.repo.id, freshData.headOid, ''],
        freshData.rootContent
      );
    }

    if (freshData.branches) {
      queryClient.setQueryData(
        ['vcs-branches', org.slug, freshData.repo.id, freshData.repo.defaultBranch],
        freshData.branches
      );
    }

    return { changed: freshData.headOid !== prevHeadOid };
  }, [queryClient, org.slug, name, search.ref]);

  const handleSetDefault = useCallback(
    async (branchName: string) => {
      if (settingDefaultRef.current) return;
      settingDefaultRef.current = true;
      setSetDefaultError(null);
      setSettingDefaultBranch(branchName);
      try {
        await updateRepoServerFn({
          data: {
            orgSlug: org.slug,
            repoId: repo.id,
            currentName: name,
            patch: { defaultBranch: branchName },
          },
        });
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['repo-detail-page', org.slug, name] }),
          queryClient.invalidateQueries({ queryKey: ['repo', org.slug, name] }),
        ]);
      } catch (err) {
        pushError(err);
        setSetDefaultError('Failed to set default bookmark. Please try again.');
      } finally {
        settingDefaultRef.current = false;
        setSettingDefaultBranch(null);
      }
    },
    [org.slug, name, repo.id, queryClient]
  );

  if (isEmpty) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Link
            to="/$orgSlug/repositories"
            params={{ orgSlug: org.slug }}
            search={{}}
            className="text-mesa-muted hover:text-mesa-fg"
          >
            <CaretLeftIcon className="h-4 w-4" />
          </Link>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <BookBookmarkIcon />
            {repo.name}
          </h1>
        </div>
        <div className="border border-mesa-border p-8">
          <div className="text-center mb-6">
            <TrayIcon className="h-12 w-12 mx-auto text-mesa-muted mb-4" />
            <h2 className="text-lg font-semibold mb-2">This repository is empty</h2>
            <p className="text-mesa-muted">Get started by creating files via the SDK.</p>
          </div>
          <GettingStartedGuide
            repos={[{ id: repo.name, name: repo.name }]}
            sdkSnippetHtml={snippets.sdkSnippetHtml}
            installSnippetHtml={snippets.installSnippetHtml}
          />
        </div>
      </div>
    );
  }

  const handleLoadMore = () => {
    fetchNextPage();
  };

  const handleSelectCommit = (change: { current_commit_oid: string }) => {
    navigate({ search: (prev) => ({ ...prev, commit: change.current_commit_oid }) });
  };

  const handleBackToList = () => {
    navigate({ search: (prev) => ({ ...prev, commit: undefined }), replace: true });
  };

  const handleFileSelect = (path: string) => {
    navigate({ search: (prev) => ({ ...prev, path }), replace: true });
  };

  const handleBranchChange = (branchName: string) => {
    navigate({ search: (prev) => ({ tab: prev.tab, ref: branchName }) });
  };

  const handleTabChange = (tab: 'code' | 'diff' | 'commits') => {
    if (tab === 'commits') {
      navigate({ search: (prev) => ({ ...prev, tab, commit: undefined }) });
      return;
    }
    if (tab === 'diff') {
      if (!hasComparableBranches) return;
      if (!compareTarget) {
        const otherBranch = branches.find((b) => b.name !== currentRef);
        if (otherBranch) {
          navigate({ search: (prev) => ({ ...prev, tab, compare: otherBranch.name }) });
          return;
        }
      }
    }
    navigate({ search: (prev) => ({ ...prev, tab }) });
  };

  const handleCompareChange = (target: string) => {
    navigate({ search: (prev) => ({ ...prev, compare: target }), replace: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Link
          to="/$orgSlug/repositories"
          params={{ orgSlug: org.slug }}
          search={{}}
          className="text-mesa-muted hover:text-mesa-fg"
        >
          <CaretLeftIcon className="h-4 w-4" />
        </Link>
        <h1 className="text-xl font-semibold flex items-center gap-2 min-w-0">
          <BookBookmarkIcon className="shrink-0" />
          <span className="truncate">{repo.name}</span>
        </h1>
        <div className="ml-auto shrink-0 flex items-center gap-2">
          <BranchSelector
            branches={branches}
            currentBranch={currentRef}
            onBranchChange={handleBranchChange}
            onSetDefault={handleSetDefault}
            settingDefaultBranch={settingDefaultBranch}
          />
          <AutoRefresh onRefresh={handleRefresh} />
        </div>
      </div>

      {setDefaultError && (
        <div className="flex items-center gap-2 text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          <WarningCircleIcon className="h-4 w-4 shrink-0" />
          <span>{setDefaultError}</span>
          <button
            type="button"
            className="ml-auto text-red-400 hover:text-red-300"
            onClick={() => setSetDefaultError(null)}
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-mesa-border">
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeTab === 'code'
              ? 'border-mesa-accent text-mesa-fg'
              : 'border-transparent text-mesa-muted hover:text-mesa-fg'
          }`}
          onClick={() => handleTabChange('code')}
        >
          <CodeIcon className="h-4 w-4 mr-2 inline" />
          Code
        </button>
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeTab === 'commits'
              ? 'border-mesa-accent text-mesa-fg'
              : 'border-transparent text-mesa-muted hover:text-mesa-fg'
          }`}
          onClick={() => handleTabChange('commits')}
        >
          <ClockCounterClockwiseIcon className="h-4 w-4 mr-2 inline" />
          Changes
        </button>
        {hasComparableBranches && (
          <button
            type="button"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'diff'
                ? 'border-mesa-accent text-mesa-fg'
                : 'border-transparent text-mesa-muted hover:text-mesa-fg'
            }`}
            onClick={() => handleTabChange('diff')}
          >
            <GitDiffIcon className="h-4 w-4 mr-2 inline" />
            Compare
          </button>
        )}
      </div>

      {activeTab === 'code' && contentResponse && entries.length === 0 ? (
        <div className="border border-mesa-border p-8 text-center">
          <GitBranchIcon className="h-12 w-12 mx-auto text-mesa-muted mb-4" />
          <h2 className="text-lg font-semibold mb-2">This bookmark has no files</h2>
          <p className="text-mesa-muted mb-4">Use the bookmark picker above to browse other bookmarks.</p>
          {defaultBranch && currentRef !== defaultBranch && (
            <Button variant="outline" size="sm" onClick={() => handleBranchChange(defaultBranch)}>
              Switch to {defaultBranch}
            </Button>
          )}
        </div>
      ) : (
        activeTab === 'code' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 border border-mesa-border overflow-hidden">
              <div className="sm:w-64 sm:min-w-48 sm:max-w-[50%] sm:resize-x border-b sm:border-b-0 sm:border-r border-mesa-border bg-mesa-bg-subtle p-2 shrink-0 overflow-auto">
                {!contentResponse ? (
                  <div className="space-y-1 p-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-2 px-2 py-1.5">
                        <Skeleton className="h-4 w-4 rounded" />
                        <Skeleton className="h-4 rounded" style={{ width: `${[55, 40, 70, 35, 60, 45][i]}%` }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <FileTree
                    key={selectedOid ?? headOid}
                    entries={entries}
                    onSelect={handleFileSelect}
                    selectedPath={selectedPath}
                    orgSlug={org.slug}
                    repoId={repo.id}
                    oid={selectedOid ?? headOid ?? undefined}
                  />
                )}
              </div>

              <div className="flex-1 p-4 min-w-0">
                {selectedPath ? (
                  <FileViewer
                    orgSlug={org.slug}
                    repo={repo.name}
                    repoId={repo.id}
                    path={selectedPath}
                    oid={selectedOid ?? headOid ?? undefined}
                  />
                ) : (
                  <div className="text-center text-mesa-muted py-8">
                    <FileIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Select a file to view its contents</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      )}

      {activeTab === 'commits' && (
        <div>
          {changeFromList ? (
            <FaroErrorBoundary key={search.commit} fallback={<CommitErrorFallback onBack={handleBackToList} />}>
              <ChangeDetail orgSlug={org.slug} repoId={repo.id} change={changeFromList} />
            </FaroErrorBoundary>
          ) : search.commit && !selectedCommit && commitFetchError ? (
            <div className="text-center py-12 border border-mesa-border">
              <WarningCircleIcon className="h-8 w-8 mx-auto mb-2 text-mesa-muted" />
              <p className="text-mesa-muted mb-4">Failed to load commit</p>
              <Button variant="outline" size="sm" onClick={handleBackToList}>
                Back to commits
              </Button>
            </div>
          ) : search.commit && !selectedCommit ? (
            <CommitDetailSkeleton />
          ) : selectedCommit ? (
            <FaroErrorBoundary key={search.commit} fallback={<CommitErrorFallback onBack={handleBackToList} />}>
              <CommitDetail orgSlug={org.slug} repoId={repo.id} commit={selectedCommit} />
            </FaroErrorBoundary>
          ) : (
            <ChangeList
              changes={allChanges}
              hasMore={!!hasNextPage}
              onLoadMore={handleLoadMore}
              onSelectChange={handleSelectCommit}
              orgSlug={org.slug}
              repoId={repo.id}
              loading={commitsFetching}
            />
          )}
        </div>
      )}

      {activeTab === 'diff' && hasComparableBranches && (
        <CompareTab
          branches={branches}
          compareBase={compareBase}
          compareTarget={compareTarget}
          currentRef={currentRef}
          onCompareChange={handleCompareChange}
          orgSlug={org.slug}
          repoId={repo.id}
        />
      )}
    </div>
  );
}

function CompareTab({
  branches,
  compareBase,
  compareTarget,
  currentRef,
  onCompareChange,
  orgSlug,
  repoId,
}: {
  branches: BranchInfo[];
  compareBase: string;
  compareTarget: string | undefined;
  currentRef: string;
  onCompareChange: (target: string) => void;
  orgSlug: string;
  repoId: string;
}) {
  const baseOid = branches.find((b) => b.name === compareBase)?.head_oid;
  const headOid = compareTarget ? branches.find((b) => b.name === compareTarget)?.head_oid : undefined;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-sm text-mesa-muted">Compare</span>
        <span className="inline-flex items-center max-w-[200px] border border-mesa-border bg-mesa-bg px-3 py-1 text-sm font-medium">
          <GitBranchIcon className="h-4 w-4 mr-2 shrink-0" />
          <span className="truncate">{compareBase}</span>
        </span>
        <span className="text-sm text-mesa-muted">with</span>
        <BranchSelector
          branches={branches.filter((branch) => branch.name !== compareBase)}
          currentBranch={compareTarget || currentRef}
          onBranchChange={onCompareChange}
        />
      </div>

      {baseOid && headOid && <DiffViewer orgSlug={orgSlug} repoId={repoId} base={baseOid} head={headOid} />}
    </div>
  );
}

function BranchSelector({
  branches,
  currentBranch,
  onBranchChange,
  onSetDefault,
  settingDefaultBranch,
}: {
  branches: BranchInfo[];
  currentBranch: string;
  onBranchChange: (branch: string) => void;
  onSetDefault?: (branch: string) => void;
  settingDefaultBranch?: string | null;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="max-w-[200px]">
          <GitBranchIcon className="h-4 w-4 mr-2 shrink-0" />
          <span className="truncate">{currentBranch}</span>
          <CaretDownIcon className="h-4 w-4 ml-2 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {branches.map((branch) => (
          <DropdownMenuItem
            key={branch.name}
            className="flex items-center justify-between"
            onClick={() => onBranchChange(branch.name)}
          >
            <div className="flex items-center">
              <CheckIcon className={`h-4 w-4 mr-2 ${branch.name === currentBranch ? 'opacity-100' : 'opacity-0'}`} />
              {branch.name}
              {branch.is_default && <span className="ml-2 text-xs text-mesa-muted">(default)</span>}
            </div>
            {onSetDefault && !branch.is_default && (
              <button
                type="button"
                className="ml-4 w-[90px] text-right text-xs text-mesa-muted hover:text-mesa-fg disabled:opacity-40"
                disabled={!!settingDefaultBranch}
                onClick={(e) => {
                  e.stopPropagation();
                  onSetDefault(branch.name);
                }}
              >
                {settingDefaultBranch === branch.name ? 'Setting...' : 'Set as default'}
              </button>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
